import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Users } from 'lucide-react';
import CTA from '../../components/CTA';

const NewtownardsWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Management Newtownards | Emotional Eating Hypnotherapy County Down</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Newtownards from Belfast clinic (12 miles). Address emotional eating, commuter stress eating, work-life balance patterns. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Newtownards, emotional eating Newtownards, weight management County Down, stress eating Newtownards, mindful eating Newtownards" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newtownards-weight-management/" />
        <meta property="og:title" content="Weight Management Newtownards | Emotional Eating Hypnotherapy County Down" />
        <meta property="og:description" content="Weight management hypnotherapy serving Newtownards. Address emotional eating, stress eating patterns, sustainable lifestyle change." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Newtownards",
            "description": "CNHC registered weight management hypnotherapy serving Newtownards from Belfast clinic. Specializing in emotional eating, commuter stress eating, and sustainable behavior change without diet culture.",
            "url": "https://www.sharonclare-hypnotherapy.com/newtownards-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Newtownards, Northern Ireland",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy in Newtownards
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Address emotional eating and commuter stress eating patterns with compassionate, evidence-based support
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">12 miles</span> from Newtownards via A20 • <span className="font-semibold">20-25 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for Newtownards residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management for Newtownards
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Newtownards community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, located just 12 miles west via the A20. The journey takes only 20-25 minutes, making this one of the most accessible locations for regular in-person weight management sessions. Newtownards residents benefit from ultra-convenient access to specialist hypnotherapy support without the long travel times faced by more distant communities.
              </p>
              <p>
                Weight struggles in Newtownards often connect to the unique pressures of commuter town life and work-life balance challenges. Many residents commute to Belfast for professional work, spending hours each week traveling to and from the office. If you find yourself eating breakfast in the car during your commute, snacking at your desk to cope with work pressure, or comfort eating in the evening to decompress from the workday and commute stress, you're experiencing emotional eating - and this isn't about willpower or lack of discipline.
              </p>
              <p>
                Solution-focused hypnotherapy for weight management works differently from diets. Instead of focusing on restriction, calorie counting, and food rules that eventually fail, we address why you eat when you're not physically hungry. By calming your stress response, building emotional resilience around work and commuter pressure, and changing your relationship with food at a subconscious level, you can achieve sustainable behavior change without the cycle of deprivation and rebound that characterizes most diets. My approach is compassionate, non-judgmental, and focused on long-term lifestyle change rather than unrealistic quick fixes or diet culture promises.
              </p>
              <p>
                Many Newtownards residents work in professional roles - office jobs, healthcare, education, business services - often requiring daily commutes to Belfast or other employment centers. The combination of sedentary desk work and commuter stress creates specific eating patterns. Sitting at a computer all day with easy access to office snacks leads to mindless grazing. The afternoon energy slump triggers sugar cravings. Work deadlines and pressure cause stress snacking without awareness. Then the evening commute adds more stress, and you find yourself eating to decompress rather than because you're genuinely hungry.
              </p>
              <p>
                Hypnotherapy helps you develop healthier coping strategies that don't involve food. By addressing the stress of commuting, desk work, and the challenge of maintaining work-life balance in a commuter town, we can break the automatic connection between work pressure and eating. When your nervous system learns to stay calm despite deadlines and traffic, when you have better tools for managing the transition between work and home, food loses its power as your primary stress relief mechanism. The changes happen at a subconscious level, so they feel natural rather than forced.
              </p>
              <p>
                For Newtownards clients who prefer the convenience of remote sessions despite the short distance to Belfast, I also offer secure online hypnotherapy via video call. Online sessions provide the same professional, evidence-based treatment with maximum flexibility. This option has been particularly valuable for clients working from home, those with unpredictable work schedules, or anyone who wants to eliminate even the short 20-minute commute. The therapeutic effectiveness is equivalent to in-person sessions while offering ultimate convenience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Emotional Eating */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              Understanding Commuter Stress Eating
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As someone who has worked extensively with professional communities throughout Northern Ireland, I understand the specific stressors that contribute to emotional eating patterns in Newtownards. The daily commute to Belfast or other employment centers creates what I call "commuter stress eating" - eating breakfast in the car because you're running late, keeping snacks in the car for the journey, stopping for coffee and pastries on the way to work, grabbing convenience food on the way home because you're too tired to cook after the commute. These aren't character flaws - they're predictable responses to time pressure and chronic low-level stress.
              </p>
              <p>
                For those working in Belfast offices, the professional desk job environment creates its own eating challenges. Sitting at a computer all day with minimal movement, often stressed by deadlines and work demands, snacking becomes an automatic stress relief mechanism and a way to break the monotony. The office kitchen with its biscuit tin, birthday cakes, and treats. Vending machines offering quick sugar hits during the afternoon slump. Lunch eaten at your desk while working, barely tasting the food. Evening drinks or dinner out with colleagues, where food becomes social bonding rather than nourishment.
              </p>
              <p>
                Work-life balance challenges in commuter towns also drive eating patterns. When you spend hours commuting each week, when work bleeds into evening hours via email and calls, when you're too exhausted from the workday to exercise or cook healthy meals, food becomes one of the few sources of pleasure and stress relief. You deserve to decompress after a demanding day, and eating provides instant gratification when you're too tired for healthier self-care activities. The problem is that this pattern creates weight gain and guilt, which creates more stress, which drives more emotional eating.
              </p>
              <p>
                The professional demographic in Newtownards often means high health awareness but time scarcity. You know what you should be doing - meal prep, regular exercise, mindful eating - but the reality of commuting, work pressure, and family responsibilities makes consistent healthy behavior feel impossible. When stress is chronic and time is limited, willpower depletes quickly. This is why diets fail - they require constant willpower and perfect execution, which is unrealistic when you're managing demanding professional and family responsibilities.
              </p>
              <p>
                What all these patterns have in common is that they're driven by stress and time pressure, not by physical hunger. Your subconscious mind has learned that food provides quick relief from work stress, commuter frustration, or the accumulated pressure of juggling career and life. The problem is that emotional eating creates its own cycle: work stress leads to eating, which leads to weight gain and guilt, which leads to more stress and shame, which leads to more eating. Breaking this cycle requires addressing the stress at its root, not just trying to "eat less and move more."
              </p>
              <p>
                Hypnotherapy interrupts this cycle by teaching your nervous system to calm down, reducing the baseline stress that drives emotional eating. When you have healthier ways to manage difficult emotions - quick stress relief techniques you can use at your desk, better strategies for managing the work-home transition, improved sleep despite work pressure - food loses its power as your primary coping mechanism. When your subconscious beliefs about food, stress, and self-care shift from depletion and guilt to balance and compassion, sustainable behavior change becomes possible even when work remains demanding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How Hypnotherapy Helps with Weight Management
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Addresses Emotional Eating
                </h3>
                <p className="text-gray-700">
                  When work stress, commuter pressure, or deadline anxiety trigger eating, it's your subconscious mind running old patterns. Hypnotherapy accesses that subconscious level, breaking the connection between professional stress and eating for comfort. You learn healthier ways to manage work pressure, commuting challenges, and work-life balance that don't involve food, creating lasting change rather than temporary willpower.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Calms Stress Response
                </h3>
                <p className="text-gray-700">
                  When your body is stuck in stress mode from daily commuting, work deadlines, or the challenge of balancing career and family, hunger hormones increase, cravings intensify, and your brain becomes hyper-focused on high-calorie foods. By teaching your nervous system to switch off the stress response even during busy workdays, hypnotherapy naturally reduces cravings and emotional eating urges without requiring constant willpower.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <Users className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Sustainable Behavior Change
                </h3>
                <p className="text-gray-700">
                  Diets work short-term but fail long-term because they rely on willpower and restriction. Hypnotherapy creates lasting change by addressing the root causes of overeating - commuter stress, work pressure, time scarcity, and subconscious beliefs about food and self-care. The changes feel natural, not forced, making sustainable weight management genuinely achievable even when professional demands remain high.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              My Approach to Weight Management
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 mb-4">
                I don't make promises about weight loss numbers, and I don't provide meal plans or diet advice. My focus is entirely on the psychological and emotional aspects of eating. You already know what healthy eating looks like - the problem isn't lack of information, it's that work stress, commuter pressure, and time scarcity override that knowledge.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Instead of telling you what to eat or promising you'll lose a specific amount of weight, I work with you to understand why you eat when you're not physically hungry. What work triggers send you reaching for desk snacks? How does commuter stress affect your eating patterns? What subconscious beliefs about stress relief, self-care, and deserving pleasure are keeping you stuck? When we address these underlying drivers, your relationship with food changes naturally.
              </p>
              <p className="text-lg text-gray-700">
                This approach is particularly important for professional clients in Newtownards because I understand that your challenges aren't just about "willpower" - they're about managing real work pressure, dealing with commuter stress, navigating time scarcity, and finding ways to care for yourself within the constraints of demanding careers and family responsibilities. My approach is practical, compassionate, and focused on changes that work within your actual life circumstances.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-4">
                What to Expect from Sessions
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Initial Consultation</h4>
                  <p className="text-gray-700">
                    We'll explore your weight history, eating patterns, work stress triggers, commuter habits, and goals. I'll explain how professional pressure and commuting drive eating behaviors, and how hypnotherapy works at a neurological level to create change. You'll also experience your first hypnotherapy session, leaving feeling calm and motivated.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ongoing Sessions</h4>
                  <p className="text-gray-700">
                    Most clients benefit from a package of 3 sessions, typically weekly or fortnightly depending on your schedule. Each session builds on the last, gradually reprogramming your subconscious patterns around food, work stress, and emotional eating. You'll notice changes fairly quickly - reduced desk snacking, better awareness of hunger versus stress-eating, more control around office treats, improved sleep (which dramatically affects weight), and a calmer relationship with food and your body.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Session Options for Newtownards
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  Single Session
                </h3>
                <p className="text-gray-700 mb-4">
                  One comprehensive weight management hypnotherapy session addressing your emotional eating patterns and work stress triggers.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Full assessment and consultation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Hypnotherapy session</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Take-home audio recording</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 border-2 border-accent-600">
                <div className="bg-accent-600 text-white text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">
                  MOST POPULAR
                </div>
                <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-4">
                  3-Session Package
                </h3>
                <p className="text-gray-700 mb-4">
                  Comprehensive weight management programme for sustainable behavioral change and lasting results.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Three full hypnotherapy sessions</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Progressive subconscious reprogramming</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Audio recordings for reinforcement</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-1" />
                    <span>Email support between sessions</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-xl p-6 border border-gray-200">
              <p className="text-gray-700 text-center">
                For busy professionals in Newtownards juggling careers and family, transparent pricing means no hidden costs or pressure to buy additional packages. Many clients find the 3-session package provides the best value for creating lasting change in eating patterns and stress management, with flexible scheduling to fit around work commitments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Newtownards
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Recommended)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the A20 west into Belfast via Dundonald, then south along the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> 12 miles through east Belfast
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 20-25 minutes in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Public transport:</span> Metro services run along the Ormeau Road corridor, with Botanic and City Hospital the nearest rail stations
                  </p>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Ultra-Convenient Access</h3>
                <p className="text-gray-700">
                  At just 12 miles via the A20, Newtownards has some of the most convenient access to Belfast of any regional town. The 20-25 minute journey is shorter than many Belfast residents' commute across the city. This ultra-convenient access makes regular hypnotherapy appointments genuinely practical - you can easily schedule sessions around work commitments without dedicating hours to travel. Many Newtownards clients already commute past the clinic for work, making appointments effortless to integrate into their routine.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Despite the short distance, many Newtownards clients choose online hypnotherapy sessions via video call for ultimate convenience. Online sessions provide the same professional treatment while eliminating even the 20-minute commute, allowing you to fit appointments into busy workdays, lunch breaks, or evenings without travel time. This option is ideal for those working from home, managing unpredictable schedules, or simply preferring the efficiency of online sessions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What County Down Clients Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I commute to Belfast for work and was eating constantly - breakfast in the car, snacks at my desk, comfort food in the evening to decompress. Sharon helped me understand I was stress-eating to cope with work pressure and commuter fatigue, not because I was genuinely hungry. After the 3-session package, I've broken those automatic eating patterns. The drive from Newtownards for sessions was so easy - literally on my way to work - and the changes have been worth every penny."
                </p>
                <p className="font-semibold text-gray-900">S.P., County Down - Commuter Stress Eating</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working from home meant I was constantly near the kitchen, snacking mindlessly while stressed about deadlines. Sharon's approach isn't about dieting or restriction - it's about understanding why work pressure triggers eating when you're not hungry. Online sessions from home were perfect for my schedule. The hypnotherapy helped me develop better stress management strategies and I've stopped the constant grazing. My relationship with food feels healthier and less guilt-driven."
                </p>
                <p className="font-semibold text-gray-900">M.K., North Down - Work-from-Home Stress Eating</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Juggling a demanding professional job with family responsibilities left me exhausted, and food became my only pleasure and stress relief. Sharon helped me understand that wasn't sustainable or healthy. The hypnotherapy addressed the root causes - chronic stress, time scarcity, depletion - rather than just telling me to eat less. I'm managing work-life balance better, finding other ways to decompress, and my eating patterns have changed naturally without feeling like deprivation."
                </p>
                <p className="font-semibold text-gray-900">L.R., County Down - Work-Life Balance Stress</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newtownards Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Newtownards from the Belfast clinic?
                </h3>
                <p className="text-gray-700">
                  Belfast is just 12 miles from Newtownards via the A20. The journey typically takes only 20-25 minutes by car in normal traffic conditions. This is one of the shortest distances of any regional town to the Belfast clinic, making Newtownards residents uniquely positioned for convenient regular appointments. Many Newtownards professionals already commute to or through Belfast for work, making sessions easy to schedule around existing travel patterns. The ultra-convenient access means you can fit appointments into busy schedules without significant travel time investment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I fit sessions around work commitments?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Clinic days and times vary, with early evening appointments available, providing flexibility for scheduling around professional commitments. Given Newtownards' proximity (just 20-25 minutes), many clients schedule appointments during lunch breaks, before work, or on days working from home. The short travel time makes it practical to attend sessions without disrupting your entire day. For maximum flexibility, online sessions via video call allow you to eliminate travel time entirely, making it even easier to fit hypnotherapy into demanding work schedules.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for commuter stress eating?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is particularly effective for commuter stress eating because it addresses the underlying stress response driving the behavior. When your daily commute creates chronic low-level stress, when you eat in the car or grab convenience food because you're time-pressured, when the transition between work and home triggers eating, these patterns run at a subconscious level. Hypnotherapy accesses that subconscious level, teaching your nervous system to stay calm during commuting, breaking the automatic connection between travel stress and eating, and helping you develop better strategies for managing the work-home transition that don't involve food.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does work-life imbalance affect eating?
                </h3>
                <p className="text-gray-700">
                  Work-life imbalance creates multiple drivers for emotional eating. When you're chronically stressed from juggling career and family, when you have minimal time for self-care, when you're exhausted from long work hours and commuting, food becomes one of the few sources of quick pleasure and stress relief. You deserve to decompress and feel good - the problem is when eating becomes your only coping strategy and source of pleasure. Hypnotherapy helps by addressing the chronic stress itself, building emotional resilience, improving sleep (which dramatically affects both stress levels and eating patterns), and developing healthier self-care strategies that don't depend on food. The goal is sustainable balance, not perfect control.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What's the cost?
                </h3>
                <p className="text-gray-700">
                  You can book a single session or the 3-session package. The package provides better value and ensures commitment to the process - sustainable behavior change rarely happens in just one session. For busy professionals in Newtownards, transparent pricing means no hidden costs, no pressure to buy additional sessions, and no expensive meal plans to purchase. What you pay is what you get - professional hypnotherapy support to address emotional eating and create lasting change in your relationship with food and stress.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is evening availability offered for busy professionals?
                </h3>
                <p className="text-gray-700">
                  Clinic days and times vary, with early evening appointments available to accommodate professional schedules. Given Newtownards' ultra-convenient 20-minute proximity to the clinic, many professionals find it practical to attend appointments before work, during extended lunch breaks, or in the late afternoon. For those unable to attend during clinic hours, online sessions via video call offer complete flexibility - you can schedule evening appointments from home without any travel time, making it genuinely practical to fit hypnotherapy into demanding professional and family schedules. The key is finding what works for your specific situation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default NewtownardsWeightManagement;
